import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import { v4 as uuidv4 } from 'uuid';
import { IPublicCredentials } from './security.service.types';
import wgLoginService from 'features/eiq-connect-login/service/login.service';
import { IAuthResponse, IChallengeVerify } from 'features/eiq-connect-login/service/login.types';
import { arrayBufferToBase64, base64ToArrayBuffer } from 'common/helpers/string.helper';
import communityResolverService from 'common/services/community-resolver-service/community-resolver.service';
import mobileAppVersionStore from 'common/stores/mobile-app-version/mobile-app-version';
import { Platform } from 'common/services/mobile-app-configuration-service/mobile-app-configuration-service.types';

const KEY = 'privateKey';

class SecureService {
  isBiometricAuthAvailable = async (): Promise<boolean> => {
    const result = await BiometricAuth.checkBiometry();
    const platform = mobileAppVersionStore.getPlatform();

    return result.isAvailable && platform === Platform.IOS;
  };

  remove = async () => {
    SecureStorage.removeItem(KEY);
  };

  generateAndStoreKeyPair = async (): Promise<IPublicCredentials | null> => {
    try {
      await BiometricAuth.authenticate({
        reason: 'Confirm identity to generate secure login key',
        cancelTitle: 'Cancel',
        allowDeviceCredential: false,
      });
    } catch (error: any) {
      console.log(error);
      return null;
    }

    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: 'ECDSA',
        namedCurve: 'P-256',
      },
      true,
      ['sign', 'verify'],
    );

    const exportedPrivateKey = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
    const exportedPublicKey = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);

    const credentialId = uuidv4();

    const cred = {
      key: arrayBufferToBase64(exportedPrivateKey),
      id: credentialId,
    };

    const value = JSON.stringify(cred);

    await SecureStorage.setItem(KEY, value);

    const publicKeyBase64 = arrayBufferToBase64(exportedPublicKey);

    const result = {
      credentialId: credentialId,
      publicKey: publicKeyBase64,
    };

    return result;
  };

  signChallenge = async (login: string): Promise<IAuthResponse | null> => {
    try {
      await BiometricAuth.authenticate({
        reason: 'Confirm identity to login',
        cancelTitle: 'Cancel',
        allowDeviceCredential: false,
      });
    } catch (error: any) {
      console.log(error);
      return null;
    }

    const result = await SecureStorage.getItem(KEY);

    const value = JSON.parse(result!);

    await communityResolverService.resolve(login);

    const challenge = await wgLoginService.requestChallege({ login, credentialId: value.id });

    const privateKeyBuffer = base64ToArrayBuffer(value.key);

    const privateKey = await window.crypto.subtle.importKey(
      'pkcs8',
      privateKeyBuffer,
      {
        name: 'ECDSA',
        namedCurve: 'P-256',
      },
      false,
      ['sign'],
    );

    const encoder = new TextEncoder();
    const challengeBuffer = encoder.encode(challenge);

    const signature = await window.crypto.subtle.sign(
      {
        name: 'ECDSA',
        hash: { name: 'SHA-256' },
      },
      privateKey,
      challengeBuffer,
    );

    const signatureBase64 = arrayBufferToBase64(signature);

    const verifyChallegeRequest: IChallengeVerify = {
      login: login,
      credentialId: value.id,
      challenge: challenge,
      signature: signatureBase64,
    };

    const response = await wgLoginService.verifyChallege(verifyChallegeRequest);

    return response;
  };
}

const secureService = new SecureService();
export default secureService;
